/**
 * Task Kanban View Component
 *
 * Desktop-optimized Kanban board for task management.
 * Supports drag and drop between columns to change task status.
 * Receives filters from parent TasksPage.
 *
 * @module pages/tasks/TaskKanbanView
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Card, Badge, Dropdown, Modal, Form, Button, Spinner, Nav, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../../components/common/PaginationBar';
import { getPriorityBadge } from '../../components/tasks/shared/taskBadges';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import KebabMenu from '../../components/common/KebabMenu';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { listTasks, reorderTask, assignTaskUsers } from '../../services/taskService';
import { supabase } from '../../services/supabase';
import { toast } from '../../utils/toast';
import { formatDateShort } from '../../utils/formatters';
import TaskAssigneeAvatar from '../../components/tasks/shared/TaskAssigneeAvatar';
import { useAuth } from '../../contexts/AuthContext';
import { esProyectoPersonal } from '../../utils/constants';
import './TaskKanbanView.css';

// Sortable Task Card Component
const SortableTaskCard = ({ task, getPriorityBadge, onClick, onNavigate, onAssign, openMenuId, onMenuToggle, canEditTask, canAssign }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    data: { task, type: 'task' },
  });

  // Only apply transform during active drag to prevent layout issues
  const isMenuOpen = openMenuId === task.id;
  const style = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : isMenuOpen ? 100 : 'auto',
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`kanban-card ${isDragging ? 'is-dragging' : ''}`}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Only navigate if not dragging and menu is not open
        if (!isDragging && !isMenuOpen) {
          onClick();
        }
      }}
    >
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h6 className="kanban-card-title mb-0">{task.title}</h6>
          <div className="d-flex align-items-center gap-1">
            <Badge bg={getPriorityBadge(task.priority).bg}>
              {getPriorityBadge(task.priority).text}
            </Badge>
            <KebabMenu
              label={`Acciones de la tarea ${task.title}`}
              dropdownProps={{
                show: isMenuOpen,
                onToggle: (nextShow) => onMenuToggle(nextShow ? task.id : null),
              }}
              menuProps={{ popperConfig: { strategy: 'fixed' }, style: { zIndex: 1060 } }}
              toggleStyle={{ fontSize: '0.85rem' }}
            >
              <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onNavigate(`${task._basePath || '/tareas'}/${task.id}`); }}>
                <i className="bi bi-eye me-2"></i>Ver Detalle
              </Dropdown.Item>
              {canEditTask && (
                <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onNavigate(`${task._basePath || '/tareas'}/${task.id}/editar`); }}>
                  <i className="bi bi-pencil me-2"></i>Editar
                </Dropdown.Item>
              )}
              {canAssign && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onAssign(task); }}>
                    <i className="bi bi-person-plus me-2"></i>Asignar
                  </Dropdown.Item>
                </>
              )}
            </KebabMenu>
          </div>
        </div>

        <p className="kanban-card-description text-muted mb-2">
          {task.description}
        </p>

        {/* Assignee avatars */}
        {task.assignees && task.assignees.length > 0 && (
          <div className="hex-avatar-stack mb-2">
            {task.assignees.slice(0, 3).map((assignment, idx) => {
              const user = assignment?.assigned_user || assignment?.user;
              if (!user) return null;
              return (
                <TaskAssigneeAvatar
                  key={assignment.id || idx}
                  assignee={user}
                  size="sm"
                />
              );
            })}
            {task.assignees.length > 3 && (
              <div className="hex-avatar hex-avatar-sm" style={{ background: '#6c757d', fontSize: '0.55rem' }}>
                +{task.assignees.length - 3}
              </div>
            )}
          </div>
        )}

        <div className="kanban-card-footer">
          {task.project?.title ? (
            <span className="kanban-card-project">
              <i className={`bi ${esProyectoPersonal(task.project?.project_number) ? 'bi-person-check' : 'bi-folder'} me-1`}></i>
              {esProyectoPersonal(task.project?.project_number) ? 'Mi lista personal' : task.project.title}
            </span>
          ) : (
            <span></span>
          )}
          {task.client?.company_name && (
            <span className="kanban-card-project">
              <i className="bi bi-building me-1"></i>
              {task.client.company_name}
            </span>
          )}

          {task.due_date && (
            <span className="kanban-card-date">
              <i className="bi bi-calendar me-1"></i>
              {formatDateShort(task.due_date)}
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

// Droppable Column Component
const DroppableColumn = ({ column, children, isOver }) => {
  const { setNodeRef } = useDroppable({
    id: column.id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column ${isOver ? 'is-over' : ''}`}
    >
      {children}
    </div>
  );
};

// Drag Overlay Card (shown while dragging)
const TaskCardOverlay = ({ task, getPriorityBadge }) => {
  if (!task) return null;

  return (
    <Card className="kanban-card kanban-card-overlay">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <h6 className="kanban-card-title mb-0">{task.title}</h6>
          <Badge bg={getPriorityBadge(task.priority).bg} className="ms-2">
            {getPriorityBadge(task.priority).text}
          </Badge>
        </div>
        <p className="kanban-card-description text-muted mb-2">
          {task.description}
        </p>
      </Card.Body>
    </Card>
  );
};

// Column definitions (static)
const COLUMNS = [
  { id: 'pending', title: 'Pendientes', icon: 'inbox', color: '#6c757d' },
  { id: 'in_progress', title: 'En Progreso', icon: 'arrow-repeat', color: '#0d6efd' },
  { id: 'completed', title: 'Completadas', icon: 'check-circle', color: '#198754' }
];

const COLUMN_IDS = COLUMNS.map(c => c.id);

// Mobile task card — vertical list item used inside the tab view
const MobileKanbanCard = ({ task, getPriorityBadge, onNavigate, onAssign, openMenuId, onMenuToggle, canEditTask, canAssign }) => {
  const isMenuOpen = openMenuId === task.id;
  return (
    <Card
      className="kanban-card mb-2"
      style={{ zIndex: isMenuOpen ? 100 : 'auto' }}
      onClick={() => { if (!isMenuOpen) onNavigate(`${task._basePath || '/tareas'}/${task.id}`); }}
    >
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-1">
          <h6 className="kanban-card-title mb-0">{task.title}</h6>
          <div className="d-flex align-items-center gap-1">
            <Badge bg={getPriorityBadge(task.priority).bg}>
              {getPriorityBadge(task.priority).text}
            </Badge>
            <KebabMenu
              label={`Acciones de la tarea ${task.title}`}
              dropdownProps={{
                show: isMenuOpen,
                onToggle: (nextShow) => onMenuToggle(nextShow ? task.id : null),
              }}
              menuProps={{ popperConfig: { strategy: 'fixed' }, style: { zIndex: 1060 } }}
              toggleStyle={{ fontSize: '0.85rem' }}
            >
              <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onNavigate(`${task._basePath || '/tareas'}/${task.id}`); }}>
                <i className="bi bi-eye me-2"></i>Ver Detalle
              </Dropdown.Item>
              {canEditTask && (
                <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onNavigate(`${task._basePath || '/tareas'}/${task.id}/editar`); }}>
                  <i className="bi bi-pencil me-2"></i>Editar
                </Dropdown.Item>
              )}
              {canAssign && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={(e) => { e.stopPropagation(); onMenuToggle(null); onAssign(task); }}>
                    <i className="bi bi-person-plus me-2"></i>Asignar
                  </Dropdown.Item>
                </>
              )}
            </KebabMenu>
          </div>
        </div>
        {task.description && (
          <p className="kanban-card-description text-muted mb-1">{task.description}</p>
        )}
        {task.assignees && task.assignees.length > 0 && (
          <div className="hex-avatar-stack mb-1">
            {task.assignees.slice(0, 3).map((assignment, idx) => {
              const user = assignment?.assigned_user || assignment?.user;
              if (!user) return null;
              return <TaskAssigneeAvatar key={assignment.id || idx} assignee={user} size="sm" />;
            })}
            {task.assignees.length > 3 && (
              <div className="hex-avatar hex-avatar-sm" style={{ background: '#6c757d', fontSize: '0.55rem' }}>
                +{task.assignees.length - 3}
              </div>
            )}
          </div>
        )}
        <div className="kanban-card-footer">
          {task.project?.title ? (
            <span className="kanban-card-project">
              <i className={`bi ${esProyectoPersonal(task.project?.project_number) ? 'bi-person-check' : 'bi-folder'} me-1`}></i>
              {esProyectoPersonal(task.project?.project_number) ? 'Mi lista personal' : task.project.title}
            </span>
          ) : <span></span>}
          {task.client?.company_name && (
            <span className="kanban-card-project">
              <i className="bi bi-building me-1"></i>
              {task.client.company_name}
            </span>
          )}
          {task.due_date && (
            <span className="kanban-card-date">
              <i className="bi bi-calendar me-1"></i>
              {formatDateShort(task.due_date)}
            </span>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const TaskKanbanView = ({ filters = {}, basePath = '/tareas' }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isSupervisor, isTechnician } = useAuth();

  // Igual que en las otras vistas de /tareas: `tasks` no tiene UPDATE para
  // `client`, y `task_assignments` no tiene INSERT/DELETE para `supervisor`.
  const canEditTask = isAdmin || isSupervisor || isTechnician;
  // El supervisor asigna desde la migracion 20260729030000, que creo
  // `supervisor_create_assignments` y `supervisor_delete_assignments`. Hasta
  // entonces la matriz decia "Asignar tarea: Supervisor ✅" y la base no tenia
  // la policy, asi que ofrecerselo habria sido un boton condenado a fallar.
  const canAssign = isAdmin || isTechnician || isSupervisor;
  const [activeTask, setActiveTask] = useState(null);
  const [overId, setOverId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Active tab for mobile view — default to first column with tasks
  const [activeTab, setActiveTab] = useState('pending');

  // Assignment modal state
  const [assignTask, setAssignTask] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [savingAssignees, setSavingAssignees] = useState(false);

  // Load employees when assignment modal opens
  useEffect(() => {
    if (assignTask) {
      loadEmployees();
      const currentIds = (assignTask.assignees || [])
        .filter(a => a?.user_id || a?.assigned_user?.id)
        .map(a => a.user_id || a.assigned_user?.id);
      setSelectedAssignees(currentIds);
    }
  }, [assignTask]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, role')
        .in('role', ['admin', 'technician'])
        .order('full_name');
      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Error al cargar empleados');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleToggleAssignee = (employeeId) => {
    setSelectedAssignees(prev =>
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSaveAssignees = async () => {
    setSavingAssignees(true);
    try {
      const result = await assignTaskUsers(assignTask.id, selectedAssignees);
      if (result.error) throw result.error;
      toast.success('Asignaciones actualizadas');
      setAssignTask(null);
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } catch (error) {
      console.error('Error saving assignees:', error);
      // El mensaje real importa: "sólo puede desasignar quien asignó" no es lo
      // mismo que un fallo de red, y antes los dos se veían igual.
      toast.error(error.message || 'Error al guardar asignaciones');
    } finally {
      setSavingAssignees(false);
    }
  };

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Paginación. El tablero también estaba topado en 20 tareas de 190 sin ningún
  // aviso; acá el default es más alto porque las filas se reparten entre tres
  // columnas y 20 se agotan enseguida.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  // Build query filters from parent filters
  const queryFilters = useMemo(() => {
    const apiFilters = { page, pageSize };

    if (filters.status && filters.status !== 'all') {
      apiFilters.status = filters.status;
    }
    if (filters.priority && filters.priority !== 'all') {
      apiFilters.priority = filters.priority;
    }
    if (filters.project && filters.project !== 'all') {
      apiFilters.projectId = filters.project;
    }
    if (filters.client && filters.client !== 'all') {
      apiFilters.clientId = filters.client;
    }
    if (filters.assignee && filters.assignee !== 'all') {
      apiFilters.assigneeId = filters.assignee;
    }
    if (filters.search) {
      apiFilters.search = filters.search;
    }
    if (filters.projectStatus) {
      apiFilters.projectStatus = filters.projectStatus;
    }
    if (filters.category && filters.category !== 'all') {
      apiFilters.category = filters.category;
    }

    return apiFilters;
  }, [filters, page, pageSize]);

  // Fetch tasks using React Query with filters (ver utils/serviceResult.js).
  const {
    data: tasksResponse,
    isLoading: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tasks', queryFilters],
    queryFn: () => unwrapServiceResult(listTasks(queryFilters)),
  });

  const totalCount = tasksResponse?.count || 0;

  // Búsqueda resuelta en el query (título + descripción), antes de paginar.
  const tasks = useMemo(() => {
    const allTasks = tasksResponse?.data || [];
    // Attach basePath to each task for navigation
    return allTasks.map(t => ({ ...t, _basePath: basePath }));
  }, [tasksResponse?.data, basePath]);

  // Set default active tab to first column with tasks (runs once after first load)
  const hasSetDefaultTab = React.useRef(false);
  useEffect(() => {
    if (!hasSetDefaultTab.current && tasks.length > 0) {
      hasSetDefaultTab.current = true;
      const firstWithTasks = COLUMNS.find(col => tasks.some(t => t.status === col.id));
      if (firstWithTasks) setActiveTab(firstWithTasks.id);
    }
  }, [tasks]);

  // Mutation for reordering tasks
  const reorderMutation = useMutation({
    mutationFn: ({ taskId, newPosition, newStatus }) =>
      reorderTask(taskId, newPosition, newStatus),
    onMutate: async ({ taskId, newPosition, newStatus }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['tasks', queryFilters] });

      // Snapshot previous value
      const previousTasks = queryClient.getQueryData(['tasks', queryFilters]);

      // Optimistically update
      queryClient.setQueryData(['tasks', queryFilters], (old) => {
        if (!old?.data) return old;

        const updatedTasks = [...old.data];
        const taskIndex = updatedTasks.findIndex(t => t.id === taskId);
        if (taskIndex === -1) return old;

        const task = { ...updatedTasks[taskIndex] };
        const oldStatus = task.status;

        if (newStatus && newStatus !== oldStatus) {
          // Moving to different column
          task.status = newStatus;
          task.position = newPosition;
        } else {
          // Same column reorder
          task.position = newPosition;
        }

        updatedTasks[taskIndex] = task;

        return {
          ...old,
          data: updatedTasks,
        };
      });

      return { previousTasks };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousTasks) {
        queryClient.setQueryData(['tasks', queryFilters], context.previousTasks);
      }
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  // Helper to find which column a task/id belongs to
  const findColumn = (id) => {
    // Check if id is a column id
    if (COLUMN_IDS.includes(id)) return id;

    // Find task and return its status
    const task = tasks.find(t => t.id === id);
    return task?.status || null;
  };

  // Drag handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setOpenMenuId(null); // Close any open menu when dragging
    setActiveTask(active.data.current?.task);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) {
      setOverId(null);
      return;
    }

    // Determine which column we're over
    const overColumn = findColumn(over.id);
    setOverId(overColumn);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setOverId(null);

    if (!over) return;

    const draggedTask = active.data.current?.task;
    if (!draggedTask) return;

    // Don't do anything if dropped on itself
    if (active.id === over.id) return;

    const activeColumn = draggedTask.status;
    const overColumn = findColumn(over.id);

    if (!overColumn) return;

    const isSameColumn = activeColumn === overColumn;

    // Get tasks in the target column sorted by position
    // Exclude the dragged task if moving within same column
    const columnTasks = tasks
      .filter(t => t.status === overColumn && (!isSameColumn || t.id !== draggedTask.id))
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    let newPosition;

    if (over.data.current?.type === 'task') {
      // Dropped on a task - find its index in the filtered list
      const overTask = tasks.find(t => t.id === over.id);
      if (overTask) {
        // Calculate position based on the over task's current position
        const overTaskPosition = overTask.position || 0;

        if (isSameColumn) {
          // Same column: take the position of the task we're dropping on
          newPosition = overTaskPosition;
        } else {
          // Different column: insert at that position
          newPosition = overTaskPosition;
        }
      } else {
        newPosition = columnTasks.length;
      }
    } else {
      // Dropped on column itself - add to end
      newPosition = columnTasks.length;
    }

    // Only update if something changed
    const statusChanged = !isSameColumn;
    const positionChanged = (draggedTask.position || 0) !== newPosition;

    if (statusChanged || positionChanged) {
      reorderMutation.mutate({
        taskId: draggedTask.id,
        newPosition,
        newStatus: statusChanged ? overColumn : null,
      });
    }
  };

  const handleDragCancel = () => {
    setActiveTask(null);
    setOverId(null);
  };

  // Shared Assignment Modal (used by both mobile and desktop views)
  const assignmentModal = (
    <Modal show={!!assignTask} onHide={() => setAssignTask(null)}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-person-plus me-2"></i>
          Asignar: {assignTask?.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingEmployees ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            Cargando empleados...
          </div>
        ) : employees.length === 0 ? (
          <p className="text-muted text-center py-4">No hay empleados disponibles</p>
        ) : (
          <div>
            {employees.map((emp) => (
              <Form.Check
                key={emp.id}
                type="checkbox"
                id={`kanban-assignee-${emp.id}`}
                className="mb-2 py-2 px-3 rounded"
                label={
                  <div className="d-flex align-items-center">
                    <i className="bi bi-person-circle fs-5 text-primary me-2"></i>
                    <div>
                      <div className="fw-medium">{emp.full_name}</div>
                      <small className="text-muted">
                        {emp.role === 'admin' ? 'Administrador' : 'Empleado'}
                      </small>
                    </div>
                  </div>
                }
                checked={selectedAssignees.includes(emp.id)}
                onChange={() => handleToggleAssignee(emp.id)}
              />
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100 align-items-center">
          <small className="text-muted">{selectedAssignees.length} seleccionado(s)</small>
          <div>
            <Button variant="secondary" size="sm" onClick={() => setAssignTask(null)} className="me-2">
              Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={handleSaveAssignees} disabled={savingAssignees}>
              {savingAssignees ? (
                <><Spinner animation="border" size="sm" className="me-1" />Guardando...</>
              ) : (
                <><i className="bi bi-check-lg me-1"></i>Guardar</>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  // "Falló la consulta" NO es "el tablero está vacío".
  if (isError) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error al cargar tareas</Alert.Heading>
        <p className="mb-3">{error?.message || 'Ocurrió un error inesperado'}</p>
        <Button variant="outline-danger" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  const pagination = (
    <PaginationBar
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={setPage}
      onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
      itemLabel="tareas"
    />
  );

  // Mobile view: status tabs + vertical card list
  if (isMobile) {
    const activeTasks = tasks
      .filter(t => t.status === activeTab)
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    return (
      <div className="task-kanban-view">
        {/* Status tabs */}
        <Nav
          variant="tabs"
          className="kanban-mobile-tabs mb-3"
          activeKey={activeTab}
          onSelect={(key) => setActiveTab(key)}
        >
          {COLUMNS.map(col => {
            const count = tasks.filter(t => t.status === col.id).length;
            return (
              <Nav.Item key={col.id}>
                <Nav.Link
                  eventKey={col.id}
                  className="kanban-mobile-tab-link"
                >
                  <i className={`bi bi-${col.icon} me-1`} style={{ color: col.color }}></i>
                  <span className="kanban-mobile-tab-title">{col.title}</span>
                  <Badge bg="secondary" pill className="ms-1">{count}</Badge>
                </Nav.Link>
              </Nav.Item>
            );
          })}
        </Nav>

        {/* Task list for active tab */}
        <div className="kanban-mobile-list">
          {activeTasks.length === 0 ? (
            <div className="kanban-empty">
              <i className="bi bi-inbox text-muted"></i>
              <p className="text-muted mb-0">Sin tareas en esta columna</p>
            </div>
          ) : (
            activeTasks.map(task => (
              <MobileKanbanCard
                canEditTask={canEditTask}
                canAssign={canAssign}
                key={task.id}
                task={task}
                getPriorityBadge={getPriorityBadge}
                onNavigate={navigate}
                onAssign={(t) => setAssignTask(t)}
                openMenuId={openMenuId}
                onMenuToggle={setOpenMenuId}
              />
            ))
          )}
        </div>

        {pagination}

        {assignmentModal}
      </div>
    );
  }

  // Desktop view: full Kanban board with drag and drop
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="task-kanban-view">
        <div className="kanban-board">
          {COLUMNS.map(column => {
            const columnTasks = tasks
              .filter(task => task.status === column.id)
              .sort((a, b) => (a.position || 0) - (b.position || 0));
            const isOver = overId === column.id;
            const taskIds = columnTasks.map(t => t.id);

            return (
              <DroppableColumn key={column.id} column={column} isOver={isOver}>
                <div className="kanban-column-header" style={{ borderTopColor: column.color }}>
                  <div className="d-flex align-items-center">
                    <i className={`bi bi-${column.icon} me-2`} style={{ color: column.color }}></i>
                    <h5 className="mb-0">{column.title}</h5>
                  </div>
                  <Badge bg="secondary" pill>{columnTasks.length}</Badge>
                </div>

                <div className="kanban-column-content">
                  <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                    {columnTasks.length === 0 ? (
                      <div className="kanban-empty">
                        <i className="bi bi-inbox text-muted"></i>
                        <p className="text-muted mb-0">Sin tareas</p>
                      </div>
                    ) : (
                      columnTasks.map(task => (
                        <SortableTaskCard
                          canEditTask={canEditTask}
                          canAssign={canAssign}
                          key={task.id}
                          task={task}
                          getPriorityBadge={getPriorityBadge}
                          onClick={() => navigate(`${basePath}/${task.id}`)}
                          onNavigate={navigate}
                          onAssign={(t) => setAssignTask(t)}
                          openMenuId={openMenuId}
                          onMenuToggle={setOpenMenuId}
                        />
                      ))
                    )}
                  </SortableContext>
                </div>
              </DroppableColumn>
            );
          })}
        </div>

        {pagination}
      </div>

      <DragOverlay>
        <TaskCardOverlay task={activeTask} getPriorityBadge={getPriorityBadge} />
      </DragOverlay>

      {assignmentModal}
    </DndContext>
  );
};

TaskKanbanView.propTypes = {
  filters: PropTypes.shape({
    status: PropTypes.string,
    priority: PropTypes.string,
    assignee: PropTypes.string,
    client: PropTypes.string,
    project: PropTypes.string,
    search: PropTypes.string
  }),
  basePath: PropTypes.string,
};

export default TaskKanbanView;
